import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Lock, Mail, AlertCircle } from 'lucide-react';

export const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Server connection state
  const [serverStatus, setServerStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected'

  // Check server connection on mount
  useEffect(() => {
    checkServer();
  }, []);

  const checkServer = async (retries = 3) => {
    setServerStatus('connecting');
    
    for (let i = 0; i < retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`, {
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          setServerStatus('connected');
          return;
        }
      } catch (err) {
        console.log(`Server check attempt ${i + 1}/${retries} failed`);
        if (i < retries - 1) {
          await new Promise(r => setTimeout(r, 2000)); // Wait 2s before retry
        }
      }
    }
    setServerStatus('disconnected');
  };

  // Click on logo to connect
  const handleLogoClick = () => {
    if (serverStatus !== 'connecting') {
      checkServer();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('yas_token', data.token);
        localStorage.setItem('yas_user', data.name);
        onLogin(data.token, data.name);
      } else {
        setError(data.error || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Get status text
  const getStatusText = () => {
    switch (serverStatus) {
      case 'disconnected': return 'Déconnecté';
      case 'connecting': return 'Connexion en cours...';
      case 'connected': return 'Connecté';
      default: return 'Déconnecté';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0d0b09' }}>
      <div className="w-full max-w-md p-8">
        {/* Logo - click to connect */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src="/litepremium_logo.png" 
            alt="Lite Premium"
            onClick={handleLogoClick}
            className={`h-64 w-auto transition-all duration-1000 cursor-pointer ${
              serverStatus === 'connecting' ? 'animate-slow-pulse' : ''
            }`}
            style={{ 
              filter: serverStatus === 'connected'
                ? 'drop-shadow(0 0 30px rgba(212, 175, 55, 1)) drop-shadow(0 0 60px rgba(212, 175, 55, 0.6))' 
                : serverStatus === 'connecting'
                  ? 'drop-shadow(0 0 25px rgba(212, 175, 55, 0.7))' 
                  : 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.2)) brightness(0.6)'
            }}
            title={serverStatus === 'disconnected' ? 'Cliquez pour connecter' : ''}
          />
          
          {/* Status text */}
          <span className={`mt-4 text-sm font-medium transition-all duration-300 ${
            serverStatus === 'connected' 
              ? 'text-[#D4AF37]' 
              : serverStatus === 'connecting'
                ? 'text-[#D4AF37]/70'
                : 'text-zinc-500'
          }`}>
            {getStatusText()}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center text-white mb-2">
          Connexion
        </h1>
        <p className="text-center text-zinc-500 mb-8">
          Accédez à votre espace de gestion de billets
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#D4AF37]" />
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#1a1a1a] border-[#D4AF37]/30 text-white placeholder:text-zinc-600 h-12"
              data-testid="login-email-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#D4AF37]" />
              Mot de passe
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#1a1a1a] border-[#D4AF37]/30 text-white placeholder:text-zinc-600 h-12"
              data-testid="login-password-input"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading || !email || !password || serverStatus !== 'connected'}
            className="w-full h-12 bg-[#D4AF37] hover:bg-[#D4B86A] text-black font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Connexion...
              </>
            ) : serverStatus !== 'connected' ? (
              'Serveur déconnecté'
            ) : (
              'Se connecter'
            )}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-8">
          LITE PREMIUM - Gestion de Billetterie
        </p>
      </div>
      
      {/* CSS for slow pulse animation */}
      <style>{`
        @keyframes slow-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-slow-pulse {
          animation: slow-pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
